import React, { useId } from 'react';
import { Button, Icon, Menu, Stack, Text } from '@iqueue/iq-ui';

/**
 * Выбор одного значения: подпись, триггер и меню под ним — единственный способ
 * выбора в админских формах.
 *
 * Готового Select в библиотеке нет (docs/IQ_UI_CHAT_GAPS.md §10.4), поэтому это
 * `Menu.RadioGroup` под кнопкой-триггером.
 *
 * Триггер меряется по содержимому и живёт в общем ряду с соседями (Cluster) —
 * `fill` не ставим, иначе кнопка занимает всю строку и ряд рассыпается по одной
 * колонке.
 *
 * Имя для скринридера собирается из подписи И значения (`aria-labelledby` на
 * два id): одна только подпись стёрла бы выбранное значение из имени кнопки.
 *
 * Свой id получает САМ `Menu.Trigger`, а не `Menu.Root`. Разница не косметическая:
 * триггер держит нажатую форму по `isOpenedByTrigger(n)`, где `n` — его
 * собственный id, а `Menu.Root triggerId` задаёт активный триггер у корня. Задав
 * второе, я развёл эти два id, `isOpenedByTrigger` навсегда стал false, и угол
 * переставал морфиться на открытом меню (`data-popup-open` просто не ставился).
 */
export default function Select({ label, value, onChange, options, disabled, placeholder = '—' }) {
  const labelId = useId();
  const triggerId = useId();
  const opts = (options || []).map(o => (typeof o === 'string' ? { value: o, label: o } : o));
  const current = opts.find(o => o.value === value);
  return (
    <Stack gap={100}>
      <Text variant="label-large" id={labelId} render={<span />}>{label}</Text>
      <Menu.Root>
        <Menu.Trigger
          id={triggerId}
          disabled={disabled}
          render={(
            <Button
              variant="outlined"
              aria-labelledby={`${labelId} ${triggerId}`}
              endIcon={<Icon name="arrow_drop_down" />}
            >
              {current ? current.label : placeholder}
            </Button>
          )}
        />
        <Menu.Portal>
          {/* align="start" — меню открывается от ЛЕВОГО края кнопки. По умолчанию
              Positioner центрирует popup по якорю (`align = "center"`), и для
              выбора значения это читается как «меню само по себе», а не как
              продолжение кнопки. Ширину кнопки popup не наследует: позиционер
              публикует `--anchor-width`, но подцепить её можно только своим CSS,
              а его мы не пишем — заявлено в библиотеку (§10.4). */}
          <Menu.Positioner align="start">
            <Menu.Popup>
              <Menu.RadioGroup value={value} onValueChange={(v) => onChange(v)}>
                {opts.map(o => (
                  <Menu.RadioItem key={String(o.value)} value={o.value}>{o.label}</Menu.RadioItem>
                ))}
              </Menu.RadioGroup>
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>
    </Stack>
  );
}
